import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Calendar, Clock, MapPin, Users, Heart, Share2, QrCode, Check, Edit } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import eventsApi from '../services/eventsApi';

const DBUEventPage = () => {
  const navigate = useNavigate();
  
  // RSVP Form State with transportation fields
  const [rsvpData, setRsvpData] = useState({
    name: '',
    email: '',
    phone: '',
    guests_count: '1',
    dietary_restrictions: '',
    special_requests: '',
    rsvp_status: 'attending',
    needs_transportation: false,
    pickup_location: '',
    transportation_guests_count: '',
    can_provide_rides: false,
    vehicle_capacity: '',
    transportation_notes: ''
  });
  const [isSubmittingRSVP, setIsSubmittingRSVP] = useState(false);
  const [rsvpSubmitted, setRsvpSubmitted] = useState(false);
  const [rsvpError, setRsvpError] = useState('');

  const eventId = '9a407f95-8675-4b00-8779-0a9a1d055cd8';

  // Event data for December 11th worship night
  const event = {
    id: eventId,
    title: 'EL Bethel | On Campus - WORSHIP Night',
    description: 'Join us for an evening of worship, Christmas carols, music, and stories. Experience the joy of Christmas celebration with our ministry community at Dallas Baptist University. This special worship night will feature inspiring music, heartfelt testimonies, and a time of fellowship that will prepare our hearts for the Christmas season.',
    event_date: '2025-12-11T20:00:00Z',
    start_time: '8:00 PM',
    end_time: '10:00 PM',
    venue: 'Rogers Theater, Patty & Bo Pilgrim Chapel',
    address: 'Dallas Baptist University, 3000 Mountain Creek Parkway, Dallas, TX 75211',
    featured_image: 'https://customer-assets.emergentagent.com/job_elbethel-worship/artifacts/54dm58vk_file_00000000fb1c71fdafa2f71e86b37bac.png',
    event_type: 'worship',
    max_capacity: '200',
    registration_required: true,
    status: 'active',
    created_by: 'Sean Talluri',
    special_instructions: 'Appetizers will be served at 7:45 PM. Dinner to follow after the worship service. Please arrive early for fellowship and food. Free parking is available on campus.'
  };

  useEffect(() => {
    trackEventView();
  }, []);

  const trackEventView = async () => {
    try {
      await eventsApi.trackEvent(eventId, 'view', { url: '/DBU' });
    } catch (error) {
      console.log('Analytics tracking not available');
    }
  };

  const handleRSVPInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setRsvpData({
      ...rsvpData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleRSVPSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingRSVP(true);
    setRsvpError('');

    try {
      await eventsApi.submitRSVP(eventId, rsvpData);
      setRsvpSubmitted(true);
      
      // Track RSVP analytics
      await eventsApi.trackEvent(eventId, 'rsvp', { 
        email: rsvpData.email, 
        name: rsvpData.name,
        guests_count: rsvpData.guests_count,
        source_url: '/DBU'
      });
      
    } catch (err) {
      setRsvpError('Unable to submit RSVP. Please try again or contact us at contact@elbethel.online');
      console.error('RSVP error:', err);
    } finally {
      setIsSubmittingRSVP(false);
    }
  };

  const formatEventDate = (dateString) => {
    const date = new Date(dateString);
    return {
      day: date.getDate(),
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      weekday: date.toLocaleDateString('en-US', { weekday: 'long' }),
      fullDate: date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    };
  };

  const shareEvent = async () => {
    const eventUrl = `${window.location.origin}/DBU`;
    const shareText = `🎄 Join us for ${event.title} on ${formatEventDate(event.event_date).fullDate} at ${event.start_time}! 

📍 ${event.venue}
⏰ ${event.start_time} - ${event.end_time}
🍽️ Appetizers at 7:45 PM, Dinner to follow

RSVP: ${eventUrl}`;
    
    // Track sharing action
    try {
      await eventsApi.trackEvent(eventId, 'share', { url: '/DBU', method: 'native_or_clipboard' });
    } catch (error) {
      console.log('Share tracking failed');
    }
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: event.title,
          text: shareText,
          url: eventUrl,
        });
      } catch (error) {
        // User cancelled sharing, that's ok
        console.log('Sharing cancelled by user');
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(shareText);
        alert('Event details copied to clipboard! You can now paste and share anywhere.');
      } catch (error) {
        // Manual fallback
        const textArea = document.createElement('textarea');
        textArea.value = shareText;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        alert('Event details copied to clipboard! You can now paste and share anywhere.');
      }
    }
  };

  const eventDate = formatEventDate(event.event_date);

  return (
    <div className="pt-16 lg:pt-20 pb-20 lg:pb-0">
      {/* Dynamic Meta Tags for Social Sharing */}
      <Helmet>
        <title>{event.title} | El Bethel Ministries</title>
        <meta name="description" content={event.description} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={event.title} />
        <meta property="og:description" content={event.description} />
        <meta property="og:image" content={event.featured_image} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={`${window.location.origin}/DBU`} />
        <meta property="og:type" content="event" />
        <meta property="og:site_name" content="El Bethel Ministries" />
        
        {/* Event-specific Open Graph Tags */}
        <meta property="event:start_time" content={event.event_date} />
        <meta property="event:location" content={`${event.venue}, ${event.address}`} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={event.title} />
        <meta name="twitter:description" content={event.description} />
        <meta name="twitter:image" content={event.featured_image} />
        
        {/* Additional Event Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Event",
            "name": event.title,
            "description": event.description,
            "startDate": event.event_date,
            "endDate": "2025-12-11T22:00:00Z",
            "location": {
              "@type": "Place",
              "name": event.venue,
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "3000 Mountain Creek Parkway",
                "addressLocality": "Dallas",
                "addressRegion": "TX",
                "postalCode": "75211",
                "addressCountry": "US"
              }
            },
            "image": event.featured_image,
            "organizer": {
              "@type": "Organization",
              "name": "El Bethel Ministries",
              "url": "https://elbethel.online"
            },
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock",
              "url": `${window.location.origin}/DBU`
            }
          })}
        </script>
      </Helmet>

      {/* Event Hero */}
      <section className="py-12 lg:py-20 bg-gradient-to-br from-purple-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Event Info */}
              <div>
                <div className="flex items-center space-x-4 mb-6">
                  <div className="bg-[#f17b21] text-white rounded-lg p-4 text-center">
                    <div className="text-sm font-semibold font-sans">{eventDate.month}</div>
                    <div className="text-3xl font-bold font-display">{eventDate.day}</div>
                  </div>
                  <div>
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 font-display leading-tight">
                      {event.title}
                    </h1>
                    <p className="text-lg text-gray-600 mt-2 font-sans">{eventDate.fullDate}</p>
                  </div>
                </div>

                <p className="text-xl text-gray-700 mb-8 leading-relaxed font-sans">
                  {event.description}
                </p>

                {/* Event Details */}
                <div className="space-y-4 mb-8">
                  <div className="flex items-center space-x-3">
                    <Clock className="w-6 h-6 text-[#f17b21]" />
                    <span className="text-lg font-semibold text-gray-800 font-sans">
                      {event.start_time} - {event.end_time}
                    </span>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-6 h-6 text-[#f17b21] mt-1" />
                    <div>
                      <p className="text-lg font-semibold text-gray-800 font-sans">{event.venue}</p>
                      <p className="text-gray-600 font-sans">{event.address}</p>
                    </div>
                  </div>

                  {event.registration_required && (
                    <div className="flex items-center space-x-3">
                      <Users className="w-6 h-6 text-[#f17b21]" />
                      <span className="text-lg font-semibold text-gray-800 font-sans">
                        RSVP Required
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex space-x-4">
                  <button
                    onClick={shareEvent}
                    className="border-2 border-[#f17b21] text-[#f17b21] hover:bg-[#f17b21] hover:text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans flex items-center space-x-2"
                  >
                    <Share2 className="w-5 h-5" />
                    <span>Share Event</span>
                  </button>
                  
                  <button
                    onClick={() => navigate('/events')}
                    className="text-[#f17b21] hover:text-[#d86919] font-semibold transition-colors duration-300 font-sans"
                  >
                    View All Events
                  </button>
                </div>
              </div>

              {/* Event Image & QR Code */}
              <div className="space-y-8">
                <div className="relative">
                  <img 
                    src={event.featured_image}
                    alt={event.title}
                    className="w-full h-80 lg:h-96 object-cover rounded-2xl shadow-lg"
                  />
                </div>
                
                {/* QR Code Section */}
                <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
                  <QrCode className="w-8 h-8 text-[#f17b21] mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-800 mb-4 font-display">Share This Event</h3>
                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <img 
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(window.location.origin + '/DBU')}`}
                      alt="Event QR Code"
                      className="w-48 h-48 mx-auto"
                    />
                  </div>
                  <p className="text-sm text-gray-600 font-sans">
                    Scan with your phone or share: <strong>elbethel.online/DBU</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Special Instructions */}
      {event.special_instructions && (
        <section className="py-8 lg:py-12 bg-blue-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-4 font-display">Important Information</h3>
                <p className="text-gray-700 leading-relaxed font-sans">
                  {event.special_instructions}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* RSVP Section */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {!rsvpSubmitted ? (
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-xl p-8 lg:p-12">
                <div className="text-center mb-8">
                  <Heart className="w-16 h-16 text-[#f17b21] mx-auto mb-6" />
                  <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4 font-display">
                    Reserve Your Spot
                  </h2>
                  <p className="text-lg text-gray-600 font-sans">
                    We're excited to worship with you! Please fill out the form below to RSVP.
                  </p>
                </div>

                <form onSubmit={handleRSVPSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={rsvpData.name}
                        onChange={handleRSVPInputChange}
                        placeholder="Enter your full name"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={rsvpData.email}
                        onChange={handleRSVPInputChange}
                        placeholder="your.email@example.com"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={rsvpData.phone}
                        onChange={handleRSVPInputChange}
                        placeholder="(123) 456-7890"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                        Number of Guests (Including You)
                      </label>
                      <select
                        name="guests_count"
                        value={rsvpData.guests_count}
                        onChange={handleRSVPInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                      >
                        <option value="1">1 Person</option>
                        <option value="2">2 People</option>
                        <option value="3">3 People</option>
                        <option value="4">4 People</option>
                        <option value="5">5 People</option>
                        <option value="6+">6+ People</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                      Dietary Restrictions (for dinner)
                    </label>
                    <input
                      type="text"
                      name="dietary_restrictions"
                      value={rsvpData.dietary_restrictions}
                      onChange={handleRSVPInputChange}
                      placeholder="Any allergies or dietary needs?"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                      Special Requests or Questions
                    </label>
                    <textarea
                      name="special_requests"
                      value={rsvpData.special_requests}
                      onChange={handleRSVPInputChange}
                      placeholder="Any special requests or questions about the event?"
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent resize-none font-sans"
                    />
                  </div>

                  {/* Transportation Section */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h4 className="text-lg font-bold text-gray-800 mb-4 font-display">Transportation Coordination</h4>
                    <p className="text-gray-600 text-sm mb-6 font-sans">
                      Help us coordinate rides and transportation to make sure everyone can attend!
                    </p>
                    
                    <div className="space-y-4">
                      {/* Need Transportation */}
                      <div>
                        <label className="flex items-start space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            name="needs_transportation"
                            checked={rsvpData.needs_transportation}
                            onChange={handleRSVPInputChange}
                            className="w-5 h-5 text-[#f17b21] border-gray-300 rounded focus:ring-[#f17b21] mt-1"
                          />
                          <div>
                            <p className="font-semibold text-gray-800 font-sans">I need a ride to the event</p>
                            <p className="text-sm text-gray-600 font-sans">
                              Check this if you need transportation assistance
                            </p>
                          </div>
                        </label>
                      </div>

                      {/* Transportation Details - Show if needs transportation */}
                      {rsvpData.needs_transportation && (
                        <div className="ml-8 space-y-4 pl-4 border-l-2 border-[#f17b21]">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                              Pickup Location *
                            </label>
                            <input
                              type="text"
                              name="pickup_location"
                              value={rsvpData.pickup_location}
                              onChange={handleRSVPInputChange}
                              placeholder="Where should we pick you up? (Address, intersection, or landmark)"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                              required={rsvpData.needs_transportation}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                              Transportation Guest Count
                            </label>
                            <select
                              name="transportation_guests_count"
                              value={rsvpData.transportation_guests_count}
                              onChange={handleRSVPInputChange}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                            >
                              <option value="">Same as RSVP guest count</option>
                              <option value="1">1 Person</option>
                              <option value="2">2 People</option>
                              <option value="3">3 People</option>
                              <option value="4">4 People</option>
                              <option value="5">5 People</option>
                              <option value="6+">6+ People</option>
                            </select>
                          </div>
                        </div>
                      )}

                      {/* Can Provide Rides */}
                      <div>
                        <label className="flex items-start space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            name="can_provide_rides"
                            checked={rsvpData.can_provide_rides}
                            onChange={handleRSVPInputChange}
                            className="w-5 h-5 text-[#f17b21] border-gray-300 rounded focus:ring-[#f17b21] mt-1"
                          />
                          <div>
                            <p className="font-semibold text-gray-800 font-sans">I can provide rides to others</p>
                            <p className="text-sm text-gray-600 font-sans">
                              Check this if you have space in your vehicle and can help others attend
                            </p>
                          </div>
                        </label>
                      </div>

                      {/* Vehicle Details - Show if can provide rides */}
                      {rsvpData.can_provide_rides && (
                        <div className="ml-8 space-y-4 pl-4 border-l-2 border-green-500">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                              Available Seats (not including yourself)
                            </label>
                            <select
                              name="vehicle_capacity"
                              value={rsvpData.vehicle_capacity}
                              onChange={handleRSVPInputChange}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                            >
                              <option value="">Select available seats</option>
                              <option value="1">1 Extra Seat</option>
                              <option value="2">2 Extra Seats</option>
                              <option value="3">3 Extra Seats</option>
                              <option value="4">4 Extra Seats</option>
                              <option value="5">5 Extra Seats</option>
                              <option value="6+">6+ Extra Seats</option>
                            </select>
                          </div>
                        </div>
                      )}

                      {/* Transportation Notes */}
                      {(rsvpData.needs_transportation || rsvpData.can_provide_rides) && (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                            Transportation Notes
                          </label>
                          <textarea
                            name="transportation_notes"
                            value={rsvpData.transportation_notes}
                            onChange={handleRSVPInputChange}
                            placeholder="Any additional transportation details, preferences, or coordination notes..."
                            rows={2}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent resize-none font-sans"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {rsvpError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-red-600 text-sm font-sans">{rsvpError}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmittingRSVP}
                    className="w-full bg-gradient-to-r from-[#f17b21] to-[#ff9649] hover:from-[#d86919] hover:to-[#e8853a] text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed font-sans flex items-center justify-center space-x-3"
                  >
                    {isSubmittingRSVP ? (
                      <>
                        <div className="animate-spin w-6 h-6 border-3 border-white border-t-transparent rounded-full"></div>
                        <span>Submitting RSVP...</span>
                      </>
                    ) : (
                      <>
                        <Heart className="w-6 h-6" />
                        <span>RSVP for December 11th</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            ) : (
              <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl shadow-xl p-8 lg:p-12 text-center">
                <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-8">
                  <Check className="w-12 h-12 text-white" />
                </div>
                
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-6 font-display">
                  You're All Set for DBU!
                </h2>
                
                <p className="text-xl text-gray-600 mb-8 font-sans">
                  Thank you for your RSVP! We're excited to worship with you on {eventDate.fullDate}.
                </p>

                <div className="bg-white rounded-xl p-6 mb-8 shadow-lg">
                  <h3 className="text-xl font-bold text-gray-800 mb-4 font-display">Event Reminder</h3>
                  <div className="space-y-3 text-left">
                    <div className="flex items-center space-x-3">
                      <Calendar className="w-5 h-5 text-[#f17b21]" />
                      <span className="font-sans">{eventDate.fullDate} at {event.start_time}</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <MapPin className="w-5 h-5 text-[#f17b21] mt-1" />
                      <div>
                        <p className="font-semibold font-sans">{event.venue}</p>
                        <p className="text-sm text-gray-600 font-sans">{event.address}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Users className="w-5 h-5 text-[#f17b21]" />
                      <span className="font-sans">
                        {rsvpData.guests_count} {rsvpData.guests_count === '1' ? 'person' : 'people'} attending
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-[#f17b21] to-[#ff9649] rounded-xl p-6 text-white text-center">
                    <Calendar className="w-8 h-8 mx-auto mb-4" />
                    <h4 className="text-xl font-bold mb-3 font-display">Add to Your Calendar</h4>
                    <p className="mb-4 opacity-90 font-sans text-sm">
                      Don't forget! Add this event to your calendar so you don't miss it.
                    </p>
                    <button
                      onClick={() => {
                        const startDate = '20251211T200000';
                        const endDate = '20251211T220000';
                        const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${startDate}/${endDate}&details=${encodeURIComponent(event.description + ' - RSVP: elbethel.online/DBU')}&location=${encodeURIComponent(event.venue + ', ' + event.address)}`;
                        window.open(calendarUrl, '_blank');
                      }}
                      className="bg-white text-[#f17b21] hover:bg-gray-100 px-6 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans flex items-center justify-center space-x-2 mx-auto"
                    >
                      <Calendar className="w-5 h-5" />
                      <span>Add to Calendar</span>
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      onClick={shareEvent}
                      className="bg-[#f17b21] hover:bg-[#d86919] text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans flex items-center justify-center space-x-2"
                    >
                      <Share2 className="w-5 h-5" />
                      <span>Invite Friends</span>
                    </button>
                    
                    <button
                      onClick={() => window.location.href = `/modify-rsvp?email=${encodeURIComponent(rsvpData.email)}`}
                      className="border-2 border-[#f17b21] text-[#f17b21] hover:bg-[#f17b21] hover:text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans flex items-center justify-center space-x-2"
                    >
                      <Edit className="w-5 h-5" />
                      <span>Modify RSVP</span>
                    </button>
                  </div>
                  
                  <div className="text-center">
                    <button
                      onClick={() => navigate('/')}
                      className="text-[#f17b21] hover:text-[#d86919] font-semibold transition-colors duration-300 font-sans flex items-center justify-center space-x-2 mx-auto"
                    >
                      <Heart className="w-5 h-5" />
                      <span>Explore Ministry</span>
                    </button>
                  </div>
                  
                  <div className="text-center pt-4">
                    <p className="text-gray-600 text-sm font-sans">
                      Questions about the event? Email us at{' '}
                      <a href="mailto:contact@elbethel.online" className="text-[#f17b21] hover:text-[#d86919] font-semibold">
                        contact@elbethel.online
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default DBUEventPage;